import { ArrowLeftIcon, Pencil1Icon } from "@radix-ui/react-icons";
import {
  Auth0AccountRole,
  FormDetail,
  FormFilledRow,
  FormsService,
  FormWaitingRow,
} from "client/openapi";
import { Button, ButtonColor, ButtonSize } from "components/Button";
import { Dialog } from "components/Dialog";
import { errorText } from "components/Forms/formsApi";
import InquiryLinks from "components/Forms/InquiryLinks";
import Pager from "components/Dashboard/PeoplePanel/Pager";
import { usePaged } from "components/Forms/usePaged";
import ViewButton from "components/Forms/ViewButton";
import ResponseDialog, { formatDate } from "components/Forms/ResponseDialog";
import { Tag } from "components/Tag/TagChip";
import { TagColor, TagSize } from "components/Tag";
import { OrgAdminShell } from "pages/org-performance/OrgAdminPage";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Notifications from "util/notifications";

const ROLE_TAGS: Record<string, { label: string; color: TagColor }> = {
  [Auth0AccountRole.PARENT]: { label: "Parent", color: TagColor.BLUE },
  [Auth0AccountRole.ME]: { label: "Student", color: TagColor.GREEN },
  [Auth0AccountRole.ORG_TUTOR]: { label: "Tutor", color: TagColor.GOLD },
};

const PROFILE_PATHS: Record<string, string> = {
  [Auth0AccountRole.PARENT]: "parents",
  [Auth0AccountRole.ME]: "students",
  [Auth0AccountRole.ORG_TUTOR]: "tutors",
};

function Person({
  personType,
  personId,
  name,
}: {
  personType: Auth0AccountRole;
  personId: number;
  name: string;
}) {
  const tag = ROLE_TAGS[personType];
  return (
    <span className="inline-flex items-center gap-2">
      <Link
        to={`/manage/${PROFILE_PATHS[personType]}/${personId}`}
        className="font-medium text-gray-900 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {name}
      </Link>
      <Tag item={tag.label} color={tag.color} size={TagSize.MINI} />
    </span>
  );
}

function Responses() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const id = Number(formId);
  const [form, setForm] = useState<FormDetail>();
  const [filled, setFilled] = useState<FormFilledRow[]>();
  const [waiting, setWaiting] = useState<FormWaitingRow[]>();
  const [view, setView] = useState<"filled" | "waiting">("filled");
  const [openResponse, setOpenResponse] = useState<number | null>(null);
  const filledPage = usePaged(filled);
  const waitingPage = usePaged(waiting);

  useEffect(() => {
    FormsService.getForm({ formId: id })
      .then(setForm)
      .catch((e) =>
        Notifications.error(errorText(e, "This form could not be loaded."))
      );
    // A failed load says so; it is never shown as an empty list.
    FormsService.getFormResponses({ formId: id })
      .then(setFilled)
      .catch((e) =>
        Notifications.error(
          errorText(e, "Filled-out forms could not be loaded.")
        )
      );
    FormsService.getFormWaitingList({ formId: id })
      .then(setWaiting)
      .catch((e) =>
        Notifications.error(
          errorText(e, "The waiting list could not be loaded.")
        )
      );
  }, [id]);

  const pill = (key: "filled" | "waiting", label: string) => (
    <button
      className={`px-4 py-2 rounded-full text-sm transition-colors duration-200 ${
        view === key
          ? "bg-[#98acfc] text-white"
          : "text-[#98acfc] hover:bg-[#98acfc]/10"
      }`}
      onClick={() => setView(key)}
    >
      {label}
    </button>
  );

  return (
    <div className="py-6 px-7 rounded-xl bg-neutral-50">
      <button
        onClick={() => navigate("/forms")}
        className="flex items-center gap-1 text-sm font-semibold text-gray-700"
      >
        <ArrowLeftIcon /> Forms
      </button>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-bold">{form?.name || " "}</h2>
        <span className="inline-flex flex-wrap gap-2">
          {form?.public_id && (
            <InquiryLinks publicId={form.public_id} formName={form.name} />
          )}
          <Button
            color={ButtonColor.PURPLE}
            size={ButtonSize.SMALL}
            onClick={() => navigate(`/forms/${id}/edit`)}
          >
            <Pencil1Icon /> Edit form
          </Button>
        </span>
      </div>

      <div className="mt-5 inline-flex rounded-full border border-[#98acfc] p-1">
        {pill(
          "filled",
          filled ? `Filled out (${filled.length})` : "Filled out"
        )}
        {pill("waiting", waiting ? `Waiting (${waiting.length})` : "Waiting")}
      </div>

      {view === "filled" && (
        <div className="mt-4 overflow-x-auto">
          {filled && filled.length === 0 ? (
            <p className="py-6 text-sm text-gray-600">
              Nobody has filled out this form yet.
            </p>
          ) : (
            <>
              {filledPage.paged && (
                <div className="mb-3">
                  <Pager
                    page={filledPage.page}
                    pageCount={filledPage.pageCount}
                    onPage={filledPage.setPage}
                  />
                </div>
              )}
              <table className="w-full border-collapse bg-white rounded-lg">
                <thead>
                  <tr className="border-b-2 border-gray-300">
                    <th className="text-left p-3 text-sm font-bold">
                      Filled out
                    </th>
                    <th className="text-left p-3 text-sm font-bold">Person</th>
                    <th className="text-left p-3 text-sm font-bold">Sent by</th>
                    <th className="text-left p-3 text-sm font-bold">
                      Responses
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Only the View button opens a response. */}
                  {filledPage.pageRows.map((row) => (
                    <tr
                      key={row.response_id}
                      className="border-b border-gray-200"
                    >
                      <td className="p-3 text-sm text-gray-700">
                        {formatDate(row.submitted_at)}
                      </td>
                      <td className="p-3 text-sm">
                        <Person
                          personType={row.person_type}
                          personId={row.person_id}
                          name={row.person_name}
                        />
                      </td>
                      <td className="p-3 text-sm text-gray-700">
                        {row.sent_by_name || ""}
                      </td>
                      <td className="p-3 text-sm text-gray-700">
                        <ViewButton
                          label={`View ${row.person_name}'s responses`}
                          onClick={() => setOpenResponse(row.response_id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {view === "waiting" && (
        <div className="mt-4 overflow-x-auto">
          {waiting && waiting.length === 0 ? (
            <p className="py-6 text-sm text-gray-600">
              Nobody is waiting on this form.
            </p>
          ) : (
            <>
              {waitingPage.paged && (
                <div className="mb-3">
                  <Pager
                    page={waitingPage.page}
                    pageCount={waitingPage.pageCount}
                    onPage={waitingPage.setPage}
                  />
                </div>
              )}
              <table className="w-full border-collapse bg-white rounded-lg">
                <thead>
                  <tr className="border-b-2 border-gray-300">
                    <th className="text-left p-3 text-sm font-bold">Person</th>
                    <th className="text-left p-3 text-sm font-bold">
                      Last sent
                    </th>
                    <th className="text-left p-3 text-sm font-bold">Opened</th>
                    <th className="text-left p-3 text-sm font-bold">Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {waitingPage.pageRows.map((row) => (
                    <tr key={row.send_id} className="border-b border-gray-200">
                      <td className="p-3 text-sm">
                        <Person
                          personType={row.person_type}
                          personId={row.person_id}
                          name={row.person_name}
                        />
                      </td>
                      <td className="p-3 text-sm text-gray-700">
                        {formatDate(row.last_sent_at)}
                      </td>
                      <td className="p-3 text-sm text-gray-700">
                        {row.opened_at ? formatDate(row.opened_at) : "Not yet"}
                      </td>
                      <td className="p-3 text-sm text-gray-700">
                        {row.expired ? (
                          <span className="text-red-500">Expired</span>
                        ) : (
                          formatDate(row.expires_at)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      <Dialog
        open={openResponse !== null}
        onOpenChange={(open) => !open && setOpenResponse(null)}
      >
        {openResponse !== null && <ResponseDialog responseId={openResponse} />}
      </Dialog>
    </div>
  );
}

export default function FormResponsesPage() {
  return <OrgAdminShell>{() => <Responses />}</OrgAdminShell>;
}
